import { groupResultsByContinent, hashAutocompleteResults } from 'app/functions/location';
import {
  AutocompleteResult,
  AutocompleteResultStation,
  SearchMaskDirection,
} from 'app/models/types/store/location';
import {
  popularCitiesSelector,
  recentCitiesSelector,
  autocompleteFilteredSelector,
  continentAutocompleteOrderSelector,
} from 'app/store/selectors';
import { useMemo } from 'react';
import { useAppSelector } from 'app/hooks/appHooks';
import { useTranslations } from './useTranslations';
import { ListBoxGroupType, ListBoxOptionType } from '@flixbus/honeycomb-react';

export type ResultsType = 'autocomplete' | 'recent' | 'popular';

type ResultsPropTypes = {
  direction: SearchMaskDirection;
  hasQuery: boolean;
  isFocused: boolean;
};

export const useResults = ({
  direction,
  hasQuery = false,
  isFocused = false,
}: ResultsPropTypes) => {
  const { getTranslation } = useTranslations({}, true);
  const popularCities = useAppSelector(popularCitiesSelector)[direction];
  const recentCities = useAppSelector(recentCitiesSelector)[direction];
  const autocompleteCities = useAppSelector(autocompleteFilteredSelector)[direction];
  const continentOrder = useAppSelector(continentAutocompleteOrderSelector);

  const resultsHash = hashAutocompleteResults([
    ...popularCities,
    ...recentCities,
    ...autocompleteCities,
  ]);
  const getSubtitle = (city: AutocompleteResult) =>
    `${city.district ? `${city.district}, ` : ''}${
      getTranslation(city.country)
        ? getTranslation(city.country)
        : city.country
          ? city.country.toUpperCase()
          : // @ts-expect-error ignore
            city.countryCode.toUpperCase()
    }`;

  const mapOptions = (results: Map<string, AutocompleteResult[]>, type: ResultsType) => {
    const citiesById = {};
    let currentIndex = 0;
    const autocompleteOptions = [...results.keys()].reduce(
      (acc: ListBoxGroupType<ListBoxOptionType>[], curr: string) => {
        const cities =
          results.get(curr)?.reduce((acc: ListBoxOptionType[], curr: AutocompleteResult) => {
            citiesById[curr.id] = {
              ...curr,
              positionOnList: currentIndex,
              filteredStations: curr.stations,
            };
            currentIndex += 1;
            const areStationsAvailable =
              curr.stations?.length && curr.stations?.length > 1 && type === 'autocomplete';
            let transformedStations: AutocompleteResultStation[] = [];
            if (areStationsAvailable && curr.stations?.length) {
              transformedStations = curr.stations;
            }

            //station mapping for autocomplete option
            const stations = areStationsAvailable
              ? transformedStations.map((station) => {
                  citiesById[station.id] = {
                    station,
                    isStation: true,
                    cityId: curr.id,
                    positionOnList: currentIndex,
                  };
                  currentIndex += 1;
                  return {
                    value: station.id,
                    title: station.name,
                  };
                })
              : [];

            //mapping cities to the autocomplete option and adding that city and its stations to the list of options
            acc.push(
              {
                value: curr.id,
                title: curr.name,
                subtitle: getSubtitle(curr),
              },
              ...stations,
            );
            return acc;
          }, []) || [];
        //adding a list of cities to a given group (continent)
        acc.push({
          group: curr,
          options: cities,
        });
        return acc;
      },
      [],
    );
    return {
      options: autocompleteOptions.filter(Boolean),
      citiesById,
    };
  };

  return useMemo(() => {
    let map: Map<string, AutocompleteResult[]> = new Map();
    let firstElement: AutocompleteResult | null = null;
    let type: ResultsType = 'autocomplete';

    if (recentCities.length < 2 && popularCities.length && !hasQuery) {
      map = new Map();
      type = 'popular';
      map.set(type, popularCities);
      firstElement = popularCities[0];
      const { options, citiesById } = mapOptions(map, type);

      return { results: map, type, firstElement, options, citiesById };
    }

    if (recentCities.length > 1 && !hasQuery) {
      map = new Map();
      type = 'recent';
      map.set(type, recentCities);
      firstElement = recentCities[0];
      const { options, citiesById } = mapOptions(map, type);

      return { results: map, type, firstElement, options, citiesById };
    }

    const grouped = groupResultsByContinent(autocompleteCities, continentOrder);
    for (const [key, results] of grouped) {
      map.set(key, results);
      if (!firstElement) {
        firstElement = results[0];
      }
    }

    const { options, citiesById } = mapOptions(map, type);

    return {
      type,
      options,
      citiesById,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsHash, hasQuery, isFocused]);
};
